import { useState, useEffect, useCallback, useMemo } from "react";
import { apiService } from "../api/api";
import { hasClaimsAdsAuditIssue } from "../views/YouTubeScorecard/Objects/filterOptions";

export const useYouTubeScorecardData = (
  dataType: "metadata" | "claimsAds" | "whitelist"
) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let result;
      switch (dataType) {
        case "metadata":
          result =
            await apiService.youtubeScorecard.getYouTubeScorecardMetadata();
          break;
        case "claimsAds":
          result =
            await apiService.youtubeScorecard.getYouTubeScorecardClaimsAds();
          break;
        case "whitelist":
          result =
            await apiService.youtubeScorecard.getYouTubeScorecardWhitelist();
          break;
        default:
          throw new Error(`Unknown data type: ${dataType}`);
      }

      setData(result);
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch data";
      setError(errorMessage);
      console.error(`Error fetching ${dataType} data:`, err);
    } finally {
      setLoading(false);
    }
  }, [dataType]);

  const refreshData = useCallback(() => {
    setRetryCount((prev) => prev + 1);
    fetchData();
  }, [fetchData]);

  // Claims rows saved only because of otherOwnersClaiming / claimedByAnotherOwner
  // stay out of the audit when Content ID thirdPartyClaims is empty.
  const memoizedData = useMemo(
    () =>
      dataType === "claimsAds" ? data.filter(hasClaimsAdsAuditIssue) : data,
    [data, dataType]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-retry on error (up to 3 times). Skip for claimsAds: failed oversized
  // responses were retrying in parallel and freezing the browser tab (black screen).
  useEffect(() => {
    if (dataType === "claimsAds") return;
    if (error && retryCount < 3) {
      const timer = setTimeout(() => {
        fetchData();
      }, 1000 * (retryCount + 1)); // Exponential backoff

      return () => clearTimeout(timer);
    }
  }, [error, retryCount, fetchData, dataType]);

  return {
    data: memoizedData,
    loading,
    error,
    refreshData,
    retryCount,
  };
};
